import React, { useState, useCallback } from "react";
import UserSidebar from "./components/UserSidebar";
import UserEditor from "./components/UserEditor";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";
import usePlatformUsers from "./hooks/usePlatformUsers";
import { USER_DEFAULTS } from "./constants";

const PlatformUsers = () => {
    const { items, loading, saveInternalUser } = usePlatformUsers();

    const [selectedUser, setSelectedUser] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const activeUser = selectedUser ?? (items.length > 0 ? items[0] : null);

    const handleSelectUser = useCallback(async (user) => {
        if (activeUser?.id === user.id) return;
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar",
            });
            if (!confirmed) return;
        }
        setSelectedUser(user);
        setIsDirty(false);
    }, [activeUser, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo colaborador?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar",
            });
            if (!confirmed) return;
        }
        setSelectedUser({ ...USER_DEFAULTS });
        setIsDirty(false);
    }, [isDirty]);

    const handleSave = useCallback(async (data) => {
        setIsSaving(true);
        try {
            // Shape já alinhado com o backend (fullName, platformRole, position)
            const payload = {
                uid: data.id || data.uid || undefined,
                email: data.email,
                fullName: data.fullName,
                platformRole: data.platformRole,
                position: data.position || null,
                phone: data.phone || null,
                photoUrl: data.photoUrl || null,
                status: data.status || "active",
                address: {
                    zipCode: data.zipCode || "",
                    street: data.street || "",
                    number: data.number || "",
                    neighborhood: data.neighborhood || "",
                    city: data.city || "",
                    state: data.state || "",
                    complement: data.complement || "",
                },
                ...(data.password ? { password: data.password } : {}),
            };
            const result = await saveInternalUser(payload);
            // Atualiza o selectedUser com o uid retornado para refletir que agora é um edit
            setSelectedUser({ ...data, id: result?.uid || data.id, uid: result?.uid || data.uid });
            setIsDirty(false);
        } catch {
            // toast já disparado pelo hook
        } finally {
            setIsSaving(false);
        }
    }, [saveInternalUser]);

    const handleDirtyChange = useCallback((dirty) => setIsDirty(dirty), []);

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Usuários da Plataforma" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="users-container mt-3">
                <MasterDetailLayout
                    master={
                        <UserSidebar
                            users={items}
                            activeId={activeUser?.id}
                            onSelect={handleSelectUser}
                            onCreateNew={handleCreateNew}
                            loading={loading}
                        />
                    }
                    detail={
                        <UserEditor
                            user={activeUser}
                            onSave={handleSave}
                            isSaving={isSaving}
                            onChange={handleDirtyChange}
                        />
                    }
                />
            </div>
        </React.Fragment>
    );
};

export default PlatformUsers;
